<template>
  <div id="app">
    <div class="container">
      <header>
        <h1>Apex箱子追踪器</h1>
        <div v-if="userId" class="user-id-display">
          用户ID: {{ userId }}
          <button @click="copyUserUrl" class="copy-btn">复制用户链接</button>
        </div>
      </header>
      
      <main>
        <!-- 总计统计 -->
        <div class="stats-card total-stats">
          <h2>总开启数量</h2>
          <div class="count">{{ totalOpened }}</div>
        </div>
        
        <!-- 今日统计 -->
        <div class="stats-card today-stats">
          <h2>今日开启</h2>
          <div class="count">{{ todayCount }}</div>
          <div class="add-boxes">
            <button @click="incrementToday(1)">+1</button>
            <button @click="incrementToday(5)">+5</button>
            <button @click="incrementToday(10)">+10</button>
          </div>
          <!-- 补录按钮 -->
          <div class="backfill-trigger">
            <button @click="openBackfillModal" class="backfill-btn">补录记录</button>
          </div>
        </div>
        
        <!-- 每日记录 -->
        <div class="daily-records">
          <h2>每日记录</h2>
          <div class="record-list">
            <div v-for="record in dailyRecords" :key="record.date" class="record-item">
              <span class="date">{{ formatDate(record.date) }}</span>
              <span class="count">{{ record.count }} 箱</span>
            </div>
          </div>
        </div>
      </main>
    </div>
    
    <!-- 补录模态框 -->
    <div v-if="showBackfillModal" class="modal-overlay" @click="closeBackfillModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>补录记录</h2>
          <button class="close-btn" @click="closeBackfillModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="backfill-form">
            <div class="form-group">
              <label for="backfill-date">选择日期:</label>
              <DatePicker 
                v-model="backfillDate" 
                placeholder="请选择日期"
              />
            </div>
            <div class="form-group">
              <label for="backfill-count">开启数量:</label>
              <input 
                type="number" 
                id="backfill-count" 
                v-model.number="backfillCount" 
                min="0"
                class="count-input"
              >
            </div>
            <button @click="submitBackfill" class="submit-btn">补录</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 用户ID设置模态框 -->
    <div v-if="showUserIdModal" class="modal-overlay" @click="closeUserIdModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>设置用户ID</h2>
          <button class="close-btn" @click="closeUserIdModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="user-id-form">
            <div class="form-group">
              <label for="user-id-input">请输入用户ID:</label>
              <input 
                type="text" 
                id="user-id-input" 
                v-model="newUserId" 
                placeholder="例如: player123"
                class="user-id-input"
              >
            </div>
            <button @click="setUserId" class="submit-btn">确定</button>
            <div class="generate-id">
              <button @click="generateRandomId" class="generate-btn">生成随机ID</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import DatePicker from './components/DatePicker.vue'
import layer from 'layui-layer'

export default {
  name: 'App',
  components: {
    DatePicker
  },
  setup() {
    // 数据状态
    const totalOpened = ref(0)
    const todayCount = ref(0)
    const dailyRecords = ref([])
    const backfillDate = ref('')
    const backfillCount = ref(0)
    const showBackfillModal = ref(false)
    const showUserIdModal = ref(false)
    const userId = ref('')
    const newUserId = ref('')
    
    // API基础URL - 使用代理路径
    const API_BASE_URL = '/tabs/apex'
    
    // 计算属性：今天日期
    const today = computed(() => {
      const now = new Date()
      return now.toISOString().split('T')[0]
    })
    
    // 格式化日期
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
    
    // 从URL获取用户ID
    const getUserIdFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('user') || ''
    }
    
    // 设置用户ID到URL
    const setUserIdToUrl = (id) => {
      if (id) {
        const url = new URL(window.location)
        url.searchParams.set('user', id)
        window.history.replaceState({}, '', url)
      }
    }
    
    // 生成随机用户ID
    const generateRandomId = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let result = ''
      for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      newUserId.value = result
    }
    
    // 复制用户链接
    const copyUserUrl = () => {
      const url = window.location.href
      navigator.clipboard.writeText(url).then(() => {
        layer.msg('用户链接已复制到剪贴板！');
      })
    }
    
    // 打开用户ID设置模态框
    const openUserIdModal = () => {
      newUserId.value = userId.value
      showUserIdModal.value = true
    }
    
    // 关闭用户ID设置模态框
    const closeUserIdModal = () => {
      showUserIdModal.value = false
    }
    
    // 设置用户ID
    const setUserId = () => {
      if (newUserId.value.trim()) {
        userId.value = newUserId.value.trim()
        setUserIdToUrl(userId.value)
        closeUserIdModal()
        // 创建用户并加载数据
        createUserIfNeeded().then(() => {
          fetchData()
        })
      } else {
        layer.msg('请输入有效的用户ID');
      }
    }
    
    // 检查用户是否存在
    const checkUserExists = async (userId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId}`)
        const result = await response.json()
        return result.data && result.data.exists || false
      } catch (error) {
        console.error('检查用户失败:', error)
        return false
      }
    }
    
    // 创建用户（如果不存在）
    const createUserIfNeeded = async () => {
      if (!userId.value) return
      
      try {
        // 先检查用户是否存在
        const exists = await checkUserExists(userId.value)
        if (!exists) {
          // 用户不存在，创建新用户
          const response = await fetch(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: userId.value
            })
          })
          
          if (!response.ok) {
            const errorResult = await response.json()
            throw new Error(errorResult.message || '创建用户失败')
          }
        }
      } catch (error) {
        console.error('创建用户失败:', error)
        layer.msg('创建用户失败');
      }
    }
    
    // 从API获取数据（基于用户ID）
    const fetchData = async () => {
      if (!userId.value) {
        // 如果没有用户ID，显示空数据
        totalOpened.value = 0
        todayCount.value = 0
        dailyRecords.value = []
        return
      }
      
      try {
        // 获取用户统计数据
        const statsResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/stats`)
        if (statsResponse.ok) {
          const statsResult = await statsResponse.json()
          console.log('Stats API response:', statsResult) // 调试日志
          if (statsResult.code === 0) {
            // 检查数据结构并正确提取totalOpened
            if (statsResult.data && statsResult.data.data) {
              totalOpened.value = statsResult.data.data.totalOpened || 0
            } else if (statsResult.data) {
              // 处理可能的扁平数据结构
              totalOpened.value = statsResult.data.totalOpened || 0
            } else {
              totalOpened.value = 0
            }
          } else {
            throw new Error(statsResult.message)
          }
        } else {
          throw new Error('获取统计数据失败')
        }
        
        // 获取用户每日记录
        const recordsResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/daily-records`)
        if (recordsResponse.ok) {
          const recordsResult = await recordsResponse.json()
          console.log('Records API response:', recordsResult) // 调试日志
          if (recordsResult.code === 0) {
            // 转换数据结构以匹配前端期望的格式
            dailyRecords.value = (recordsResult.data || []).map(record => ({
              date: record.recordDate,
              count: record.count
            }))
          } else {
            throw new Error(recordsResult.message)
          }
        } else {
          throw new Error('获取每日记录失败')
        }
        
        // 获取今日记录
        const todayResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/daily-records/${today.value}`)
        if (todayResponse.ok) {
          const todayResult = await todayResponse.json()
          if (todayResult.code === 0 && todayResult.data) {
            todayCount.value = todayResult.data.count
          } else {
            todayCount.value = 0
          }
        } else {
          todayCount.value = 0
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        layer.msg('获取数据失败');
        // 使用模拟数据作为后备
        totalOpened.value = 0
        todayCount.value = 0
        dailyRecords.value = [
          
        ]
      }
      
      // 设置默认补录日期为今天
      backfillDate.value = today.value
    }
    
    // 增加今日开启数量
    const incrementToday = async (amount) => {
      if (!userId.value) {
        openUserIdModal()
        return
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId.value}/open`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            count: amount
          })
        })
        
        const result = await response.json()
        console.log('Open API response:', result) // 调试日志
        if (result.code === 0) {
          todayCount.value = result.data.data.todayCount
          totalOpened.value = result.data.data.totalOpened
          
          // 更新今日记录显示
          const todayRecordIndex = dailyRecords.value.findIndex(record => record.date === today.value)
          if (todayRecordIndex !== -1) {
            dailyRecords.value[todayRecordIndex].count = todayCount.value
          } else {
            dailyRecords.value.unshift({ date: today.value, count: todayCount.value })
          }
        } else {
          throw new Error(result.message)
        }
      } catch (error) {
        console.error('增加今日开启数量失败:', error)
        layer.msg('增加今日开启数量失败');
      }
    }
    
    // 打开补录模态框
    const openBackfillModal = () => {
      if (!userId.value) {
        openUserIdModal()
        return
      }
      
      backfillDate.value = today.value
      backfillCount.value = 0
      showBackfillModal.value = true
    }
    
    // 关闭补录模态框
    const closeBackfillModal = () => {
      showBackfillModal.value = false
    }
    
    // 提交补录记录
    const submitBackfill = async () => {
      if (!userId.value) {
        openUserIdModal()
        return
      }
      
      if (!backfillDate.value || backfillCount.value < 0) {
        layer.msg('请选择日期并输入有效的数量');
        return
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId.value}/backfill`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: backfillDate.value,
            count: backfillCount.value
          })
        })
        
        const result = await response.json()
        console.log('Backfill API response:', result) // 调试日志
        if (result.code === 0) {
          totalOpened.value = result.data.data.totalOpened
          
          // 更新记录显示
          const existingRecordIndex = dailyRecords.value.findIndex(record => record.date === backfillDate.value)
          if (existingRecordIndex !== -1) {
            dailyRecords.value[existingRecordIndex].count = backfillCount.value
          } else {
            dailyRecords.value.push({
              date: backfillDate.value,
              count: backfillCount.value
            })
            
            // 按日期排序
            dailyRecords.value.sort((a, b) => new Date(b.date) - new Date(a.date))
          }
          
          // 关闭模态框并重置表单
          closeBackfillModal()
          console.log('API message:', result.message);
          console.log('Message type:', typeof result.message);
          // 显示API返回的第一级message
          const message = result.message || '补录成功！';
          console.log('Final message:', message);
          // 使用最简单的调用方式
          layer.msg(message);
        } else {
          throw new Error(result.message)
        }
      } catch (error) {
        console.error('补录记录失败:', error)
        layer.msg('补录失败');
      }
    }
    
    // 组件挂载时获取数据
    onMounted(async () => {
      // 从URL获取用户ID
      userId.value = getUserIdFromUrl()
      
      // 如果有用户ID，先检查用户是否存在
      if (userId.value) {
        try {
          const exists = await checkUserExists(userId.value)
          if (exists) {
            // 用户存在，直接加载数据
            fetchData()
          } else {
            // 用户不存在，提示用户创建
            layer.msg('用户不存在，请先创建用户');
            setTimeout(() => {
              openUserIdModal()
            }, 1000)
          }
        } catch (error) {
          console.error('检查用户失败:', error)
          layer.msg('检查用户失败');
        }
      } else {
        // 如果没有用户ID，提示用户设置
        setTimeout(() => {
          openUserIdModal()
        }, 1000)
      }
    })
    
    return {
      totalOpened,
      todayCount,
      dailyRecords,
      backfillDate,
      backfillCount,
      showBackfillModal,
      showUserIdModal,
      userId,
      newUserId,
      today,
      formatDate,
      incrementToday,
      openBackfillModal,
      closeBackfillModal,
      submitBackfill,
      openUserIdModal,
      closeUserIdModal,
      setUserId,
      generateRandomId,
      copyUserUrl
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Arial', sans-serif;
  /* Apex主题渐变背景 - 深蓝到紫色 */
  background: linear-gradient(135deg, #0d1a2d 0%, #1a0d2d 50%, #2d0d1a 100%);
  min-height: 100vh;
  color: #fff;
}

#app {
  padding: 20px;
}

.container {
  max-width: 800px;
  margin: 0 auto;
}

header {
  text-align: center;
  margin-bottom: 30px;
}

header h1 {
  color: #ffcc00; /* Apex金色标题 */
  font-size: 2.5rem;
  text-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
  letter-spacing: 1px;
  margin-bottom: 10px;
}

.user-id-display {
  color: #667eea;
  font-size: 1rem;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.copy-btn {
  background: rgba(102, 126, 234, 0.2);
  border: 1px solid rgba(102, 126, 234, 0.5);
  color: #667eea;
  padding: 4px 10px;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  background: rgba(102, 126, 234, 0.3);
  transform: translateY(-1px);
}

.stats-card {
  /* Apex主题卡片背景 */
  background: rgba(13, 26, 45, 0.85); /* 深蓝半透明 */
  border-radius: 15px;
  padding: 25px;
  margin-bottom: 25px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(102, 126, 234, 0.3); /* Apex蓝色边框 */
}

.stats-card h2 {
  color: #ffcc00; /* Apex金色文字 */
  margin-bottom: 15px;
  font-size: 1.5rem;
  text-align: center;
  text-shadow: 0 0 5px rgba(255, 204, 0, 0.3);
}

.count {
  font-size: 3rem;
  font-weight: bold;
  /* Apex蓝色数字 */
  color: #667eea;
  text-align: center;
  margin: 15px 0;
  text-shadow: 0 0 10px rgba(102, 126, 234, 0.5);
}

.add-boxes {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 20px;
}

.add-boxes button {
  /* Apex主题按钮 - 渐变紫色 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  flex: 1;
  min-width: 80px;
  max-width: 120px;
  font-weight: bold;
  letter-spacing: 1px;
}

.add-boxes button:hover {
  /* 悬停效果 */
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

/* 补录按钮 */
.backfill-trigger {
  margin-top: 20px;
  text-align: center;
}

.backfill-btn {
  background: linear-gradient(135deg, #ffcc00 0%, #ff9900 100%); /* 金色渐变 */
  color: #0d1a2d; /* 深蓝文字 */
  border: none;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
}

.backfill-btn:hover {
  background: linear-gradient(135deg, #ff9900 0%, #ffcc00 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.6);
}

.daily-records h2 {
  color: #ffcc00; /* Apex金色文字 */
  margin-bottom: 20px;
  font-size: 1.5rem;
  text-align: center;
  text-shadow: 0 0 5px rgba(255, 204, 0, 0.3);
}

.record-list {
  max-height: 400px;
  overflow-y: auto;
}

.record-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  /* Apex主题记录项背景 */
  background: rgba(26, 13, 45, 0.7);
  border-radius: 10px;
  margin-bottom: 10px;
  transition: all 0.3s ease;
  border-left: 3px solid #667eea; /* 左侧蓝色边框 */
}

.record-item:hover {
  background: rgba(26, 13, 45, 0.9);
  transform: translateX(5px);
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.5);
}

.date {
  font-weight: bold;
  color: #ffcc00; /* Apex金色日期 */
  text-shadow: 0 0 3px rgba(255, 204, 0, 0.3);
}

.record-item .count {
  font-size: 1.2rem;
  margin: 0;
  color: #667eea; /* Apex蓝色数字 */
  font-weight: bold;
  text-shadow: 0 0 5px rgba(102, 126, 234, 0.3);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: rgba(13, 26, 45, 0.95);
  border-radius: 15px;
  padding: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(102, 126, 234, 0.5);
  max-width: 90%;
  width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px 10px;
  border-bottom: 1px solid rgba(102, 126, 234, 0.3);
}

.modal-header h2 {
  color: #ffcc00;
  margin: 0;
  text-shadow: 0 0 5px rgba(255, 204, 0, 0.3);
}

.close-btn {
  background: none;
  border: none;
  color: #ffcc00;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 204, 0, 0.2);
  transform: rotate(90deg);
}

.modal-body {
  padding: 25px;
}

/* 补录功能样式 */
.backfill-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 用户ID表单样式 */
.user-id-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.generate-id {
  text-align: center;
}

.generate-btn {
  background: rgba(255, 204, 0, 0.2);
  border: 1px solid rgba(255, 204, 0, 0.5);
  color: #ffcc00;
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.generate-btn:hover {
  background: rgba(255, 204, 0, 0.3);
  transform: translateY(-2px);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #ffcc00;
  font-weight: bold;
  text-shadow: 0 0 3px rgba(255, 204, 0, 0.3);
}

.count-input, .user-id-input {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.5);
  background: rgba(26, 13, 45, 0.7);
  color: white;
  font-size: 1rem;
}

.count-input:focus, .user-id-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 8px rgba(102, 126, 234, 0.6);
}

.user-id-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.submit-btn {
  /* Apex主题按钮 - 渐变紫色 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
  align-self: center;
  min-width: 120px;
  margin-top: 10px;
}

.submit-btn:hover {
  /* 悬停效果 */
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

/* 移动端适配 */
@media (max-width: 768px) {
  #app {
    padding: 10px;
  }
  
  .container {
    padding: 0 5px;
  }
  
  header h1 {
    font-size: 2rem;
    margin-bottom: 15px;
  }
  
  .user-id-display {
    flex-direction: column;
    gap: 5px;
  }
  
  .stats-card {
    padding: 20px 15px;
    margin-bottom: 20px;
  }
  
  .stats-card h2 {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
  
  .count {
    font-size: 2.5rem;
    margin: 10px 0;
  }
  
  .add-boxes {
    gap: 10px;
    margin-top: 15px;
  }
  
  .add-boxes button {
    padding: 10px 15px;
    font-size: 0.9rem;
    min-width: 70px;
    max-width: 100px;
  }
  
  .record-item {
    padding: 12px 15px;
    margin-bottom: 8px;
  }
  
  .date {
    font-size: 0.9rem;
  }
  
  .record-item .count {
    font-size: 1rem;
  }
  
  /* 移动端补录按钮 */
  .backfill-trigger {
    margin-top: 15px;
  }
  
  .backfill-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
  
  /* 移动端模态框 */
  .modal-content {
    width: 95%;
    margin: 0 10px;
  }
  
  .modal-header {
    padding: 15px 20px 5px;
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .backfill-form, .user-id-form {
    gap: 15px;
  }
  
  .submit-btn {
    padding: 10px 15px;
    font-size: 0.9rem;
    min-width: 100px;
  }
}

/* 小屏幕适配 */
@media (max-width: 480px) {
  header h1 {
    font-size: 1.8rem;
  }
  
  .stats-card {
    padding: 15px 10px;
  }
  
  .stats-card h2 {
    font-size: 1.2rem;
  }
  
  .count {
    font-size: 2rem;
  }
  
  .add-boxes {
    gap: 8px;
  }
  
  .add-boxes button {
    padding: 8px 12px;
    font-size: 0.85rem;
    min-width: 60px;
    max-width: 90px;
  }
  
  .record-item {
    flex-direction: row;
    text-align: left;
    gap: 0;
  }
  
  .date {
    font-size: 0.85rem;
  }
  
  .record-item .count {
    font-size: 0.9rem;
  }
  
  /* 小屏幕补录表单 */
  .form-group {
    gap: 5px;
  }
  
  .count-input, .user-id-input {
    padding: 10px;
    font-size: 0.9rem;
  }
  
  .submit-btn {
    padding: 8px 12px;
    font-size: 0.85rem;
    min-width: 90px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 360px) {
  header h1 {
    font-size: 1.6rem;
  }
  
  .stats-card h2 {
    font-size: 1.1rem;
  }
  
  .count {
    font-size: 1.8rem;
  }
  
  .add-boxes button {
    padding: 6px 10px;
    font-size: 0.8rem;
    min-width: 50px;
  }
  
  .count-input, .user-id-input {
    padding: 8px;
    font-size: 0.8rem;
  }
  
  .submit-btn {
    padding: 6px 10px;
    font-size: 0.8rem;
    min-width: 80px;
  }
}
</style>